const getApiBaseUrl = () =>
  String(
    process.env.REACT_APP_POS_API_BASE_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      "",
  ).replace(/\/+$/, "");

export const OFFICE_COMPETITION_API_PATHS = {
  dashboard: "/api/office-competition/dashboard",
  groupKpi: (groupId) =>
    `/api/office-competition/groups/${encodeURIComponent(groupId)}/kpi`,
  employeeSearch: "/api/office-competition/employees/search",
  groupMembers: (groupId) =>
    `/api/office-competition/groups/${encodeURIComponent(groupId)}/members`,
  groupMember: (groupId, employeeCode) =>
    `/api/office-competition/groups/${encodeURIComponent(
      groupId,
    )}/members/${encodeURIComponent(employeeCode)}`,
  groupOrders: (groupId) =>
    `/api/office-competition/groups/${encodeURIComponent(groupId)}/orders`,
  groupOrderDetail: (groupId, orderId) =>
    `/api/office-competition/groups/${encodeURIComponent(
      groupId,
    )}/orders/${encodeURIComponent(orderId)}`,
};

export const OFFICE_COMPETITION_GROUPS = [
  { groupId: "0", groupName: "CKSH, NVKD Đơn Hàng Sỉ" },
  { groupId: "1", groupName: "Nhóm 1" },
  { groupId: "2", groupName: "Nhóm 2" },
  { groupId: "3", groupName: "Nhóm 3" },
];

const parseResponseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
};

export class OfficeCompetitionApiError extends Error {
  constructor(message, { status, data } = {}) {
    super(message);
    this.name = "OfficeCompetitionApiError";
    this.status = status;
    this.data = data;
  }
}

const buildApiUrl = (path, query = {}) => {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    throw new OfficeCompetitionApiError("Chưa cấu hình POS Backend API URL.");
  }

  const searchParams = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return `${apiBaseUrl}${path}${queryString ? `?${queryString}` : ""}`;
};

const request = async (path, { method = "GET", query, body } = {}) => {
  const token = localStorage.getItem("token");
  const response = await fetch(buildApiUrl(path, query), {
    method,
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await parseResponseBody(response);

  if (!response.ok || data?.success === false) {
    throw new OfficeCompetitionApiError(
      data?.message || `Không thể xử lý yêu cầu (HTTP ${response.status}).`,
      { status: response.status, data },
    );
  }

  return data;
};

export const fetchOfficeCompetitionDashboard = async ({
  fromDate,
  toDate,
} = {}) => {
  return request(OFFICE_COMPETITION_API_PATHS.dashboard, {
    query: { fromDate, toDate },
  });
};

export const saveOfficeCompetitionKpi = async (groupId, payload) => {
  return request(OFFICE_COMPETITION_API_PATHS.groupKpi(groupId), {
    method: "PUT",
    body: payload,
  });
};

export const deleteOfficeCompetitionKpi = async (
  groupId,
  { fromDate, toDate } = {},
) => {
  return request(OFFICE_COMPETITION_API_PATHS.groupKpi(groupId), {
    method: "DELETE",
    query: { fromDate, toDate },
  });
};

export const searchOfficeCompetitionEmployees = async (employeeCode) => {
  return request(OFFICE_COMPETITION_API_PATHS.employeeSearch, {
    query: { employeeCode },
  });
};

export const addOfficeCompetitionMember = async (
  groupId,
  employeeCodeOrCodes,
) => {
  const isBatch = Array.isArray(employeeCodeOrCodes);
  const employeeCodes = [
    ...new Set(
      (isBatch ? employeeCodeOrCodes : [employeeCodeOrCodes])
        .map((employeeCode) => String(employeeCode || "").trim().toUpperCase())
        .filter(Boolean),
    ),
  ];
  if (employeeCodes.length === 0) {
    throw new OfficeCompetitionApiError("Vui lòng nhập mã nhân viên.", {
      status: 400,
    });
  }

  return request(OFFICE_COMPETITION_API_PATHS.groupMembers(groupId), {
    method: "POST",
    body: isBatch
      ? { employeeCodes }
      : { employeeCode: employeeCodes[0] },
  });
};

export const removeOfficeCompetitionMember = async (
  groupId,
  employeeCode,
) => {
  return request(
    OFFICE_COMPETITION_API_PATHS.groupMember(groupId, employeeCode),
    { method: "DELETE" },
  );
};

export const fetchOfficeCompetitionGroupOrders = async (
  groupId,
  { fromDate, toDate } = {},
) =>
  request(OFFICE_COMPETITION_API_PATHS.groupOrders(groupId), {
    query: { fromDate, toDate },
  });

export const fetchOfficeCompetitionGroupOrderDetail = async (
  groupId,
  orderId,
  { fromDate, toDate } = {},
) =>
  request(OFFICE_COMPETITION_API_PATHS.groupOrderDetail(groupId, orderId), {
    query: { fromDate, toDate },
  });
